import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db } from '../../config/firebase.js';
import { doc, collection, getDoc, getDocs, addDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { styled } from '@mui/material/styles';
import { grey, blue, green, red } from '@mui/material/colors';
import AnalyticsPage from './AnalyticsPage.jsx';
import CreateStudentLink from './CreateStudentLink';

// MUI Components
import {
  Container,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  IconButton,
  Collapse,
  Paper,
  Avatar,
  Divider,
  Chip,
  Card,
  CardContent,
  CardActions,
  Menu,
  MenuItem,
  Tooltip,
  Badge,
  Grid,
  Tabs,
  Tab,
  AppBar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab
} from '@mui/material';

// MUI Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GradeIcon from '@mui/icons-material/Grade';
import ClassIcon from '@mui/icons-material/Class';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import PostAddIcon from '@mui/icons-material/PostAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckIcon from '@mui/icons-material/Check';
import FiberNewIcon from '@mui/icons-material/FiberNew';

// Styled Components
const ClassroomHeader = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  backgroundColor: '#ffffff',
  color: '#1a1a1a',
  borderRadius: 16,
  border: `1px solid ${grey[200]}`,
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
}));

const ClassroomCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: 16,
  border: `1px solid ${grey[200]}`,
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    borderColor: blue[200],
  },
}));

const AssignmentCard = styled(ClassroomCard)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const SubmissionItem = styled(ListItem)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: `1px solid ${grey[100]}`,
  borderRadius: 12,
  marginBottom: theme.spacing(1),
  padding: theme.spacing(2),
  '&:hover': {
    backgroundColor: grey[50],
    borderColor: blue[200],
  },
}));

const ColorChip = styled(Chip)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: `1px solid ${grey[200]}`,
  color: grey[700],
  fontWeight: 500,
  '&.MuiChip-colorPrimary': {
    backgroundColor: blue[50],
    color: blue[700],
    border: `1px solid ${blue[200]}`,
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: 12,
  border: `1px solid ${grey[200]}`,
  padding: theme.spacing(0.5),
  '& .MuiTab-root': {
    borderRadius: 8,
    margin: theme.spacing(0, 0.5),
    minHeight: 48,
    textTransform: 'none',
    fontWeight: 500,
    color: grey[600],
    '&.Mui-selected': {
      backgroundColor: blue[50],
      color: blue[700],
    },
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
}));

const ClassroomFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  backgroundColor: blue[600],
  color: '#ffffff',
  boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)',
  '&:hover': {
    backgroundColor: blue[700],
    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
  },
}));

const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: 10,
  textTransform: 'none',
  fontWeight: 500,
  padding: theme.spacing(1, 2),
  boxShadow: 'none',
  '&.MuiButton-contained': {
    backgroundColor: blue[600],
    '&:hover': {
      backgroundColor: blue[700],
      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
    },
  },
  '&.MuiButton-outlined': {
    borderColor: grey[300],
    color: grey[700],
    '&:hover': {
      borderColor: blue[300],
      backgroundColor: blue[50],
    },
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: blue[100],
  color: blue[700],
  fontWeight: 600,
}));

export default function TeacherDashboard() {
  const { collegeId } = useParams();
  const [teacherId, setTeacherId] = useState(null);
  const [assignedClasses, setAssignedClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedClassData, setSelectedClassData] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [assignmentTitle, setAssignmentTitle] = useState("");
  const [assignmentContent, setAssignmentContent] = useState("");
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [submissionsByAssignment, setSubmissionsByAssignment] = useState({});
  const [expandedAssignments, setExpandedAssignments] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setTeacherId(user.uid);
      } else {
        navigate("/login");
      }
    });

    return () => unsubscribe();
  }, [collegeId, navigate]);

  useEffect(() => {
    if (teacherId) {
      fetchTeacherData(teacherId);
    }
  }, [teacherId]);

  const fetchTeacherData = async (userId) => {
    setLoading(true);
    try {
      const teacherRef = doc(db, "colleges", collegeId, "teachers", userId);
      const teacherSnap = await getDoc(teacherRef);

      if (teacherSnap.exists()) {
        const teacherData = teacherSnap.data();
        if (teacherData.assignedClasses?.length > 0) {
          await fetchClasses(teacherData.assignedClasses);
        } else {
          console.warn("No assigned classes found.");
        }
      } else {
        console.error("Teacher document not found!");
        navigate("/login");
      }
    } catch (error) {
      console.error("Error fetching teacher data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async (classIds) => {
    try {
      const classDocs = await Promise.all(
        classIds.map(async (classId) => {
          const classRef = doc(db, "colleges", collegeId, "classes", classId);
          const classSnap = await getDoc(classRef);
          return classSnap.exists() ? { id: classSnap.id, ...classSnap.data() } : null;
        })
      );
      setAssignedClasses(classDocs.filter((cls) => cls !== null));
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  useEffect(() => {
    if (selectedClass) {
      const classData = assignedClasses.find(cls => cls.id === selectedClass);
      setSelectedClassData(classData);
      fetchAssignments();
    }
  }, [selectedClass, assignedClasses]);

  const fetchAssignments = async () => {
    if (!selectedClass) return;
    setLoading(true);
    
    try {
      const assignmentsRef = collection(
        db,
        "colleges",
        collegeId,
        "classes",
        selectedClass,
        "assignments"
      );
      const querySnapshot = await getDocs(assignmentsRef);
      const fetchedAssignments = querySnapshot.docs.map((doc) => ({ 
        id: doc.id, 
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      setAssignments(fetchedAssignments.sort((a, b) => b.createdAt - a.createdAt));
      setSubmissionsByAssignment({});
    } catch (error) {
      console.error("Error fetching assignments:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissionsForAssignment = async (assignmentId) => {
    try {
      const submissionsRef = collection(
        db,
        "colleges",
        collegeId,
        "classes",
        selectedClass,
        "assignments",
        assignmentId,
        "submissions"
      );
      const querySnapshot = await getDocs(submissionsRef);
      const submissions = querySnapshot.docs.map((doc) => ({ 
        id: doc.id, 
        ...doc.data(),
        submittedAt: doc.data().submittedAt?.toDate() || new Date()
      }));
      setSubmissionsByAssignment((prev) => ({
        ...prev,
        [assignmentId]: submissions.sort((a, b) => b.submittedAt - a.submittedAt),
      }));
    } catch (error) {
      console.error("Error fetching submissions:", error);
    }
  };

  const toggleExpand = async (assignmentId) => {
    setExpandedAssignments((prev) => ({
      ...prev,
      [assignmentId]: !prev[assignmentId],
    }));

    if (!submissionsByAssignment[assignmentId]) {
      await fetchSubmissionsForAssignment(assignmentId);
    }
  };

  const createAssignment = async () => {
    if (!assignmentTitle.trim() || !assignmentContent.trim() || !selectedClass) return;
    setLoading(true);

    try {
      const assignmentsRef = collection(
        db,
        "colleges",
        collegeId,
        "classes",
        selectedClass,
        "assignments"
      );
      await addDoc(assignmentsRef, {
        title: assignmentTitle,
        content: assignmentContent,
        createdAt: new Date(),
      });

      setAssignmentTitle("");
      setAssignmentContent("");
      setAssignmentDialogOpen(false);
      await fetchAssignments();
    } catch (error) {
      console.error("Error creating assignment:", error);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteAssignment = (assignmentId) => {
    setAssignmentToDelete(assignmentId);
    setDeleteDialogOpen(true);
  };

  const deleteAssignment = async () => {
    if (!assignmentToDelete) return;
    setLoading(true);

    try {
      const assignmentRef = doc(
        db,
        "colleges",
        collegeId,
        "classes",
        selectedClass,
        "assignments",
        assignmentToDelete
      );
      await deleteDoc(assignmentRef);
      await fetchAssignments();
    } catch (error) {
      console.error("Error deleting assignment:", error);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setAssignmentToDelete(null);
    }
  };

  const startEditingAssignment = (assignment) => {
    setEditingAssignment(assignment);
    setAssignmentTitle(assignment.title);
    setAssignmentContent(assignment.content);
    setAssignmentDialogOpen(true);
  };

  const cancelEditing = () => {
    setEditingAssignment(null);
    setAssignmentTitle("");
    setAssignmentContent("");
    setAssignmentDialogOpen(false);
  };

  const updateAssignment = async () => {
    if (!editingAssignment) return;
    setLoading(true);

    try {
      const assignmentRef = doc(
        db,
        "colleges",
        collegeId,
        "classes",
        selectedClass,
        "assignments",
        editingAssignment.id
      );
      await updateDoc(assignmentRef, {
        title: assignmentTitle,
        content: assignmentContent,
      });

      setEditingAssignment(null);
      setAssignmentTitle("");
      setAssignmentContent("");
      setAssignmentDialogOpen(false);
      await fetchAssignments();
    } catch (error) {
      console.error("Error updating assignment:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openNewAssignmentDialog = () => {
    setEditingAssignment(null);
    setAssignmentTitle("");
    setAssignmentContent("");
    setAssignmentDialogOpen(true);
  };

  return (
    <Box sx={{ backgroundColor: '#fafafa', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress sx={{ color: blue[600] }} />
          </Box>
        )}

        {!selectedClass ? (
          <Box>
            <ClassroomHeader elevation={0}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box 
                  sx={{ 
                    backgroundColor: blue[100], 
                    borderRadius: '50%', 
                    p: 1.5, 
                    mr: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <DashboardIcon sx={{ fontSize: 32, color: blue[700] }} />
                </Box>
                <Box>
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}>
                    Teacher Dashboard
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: grey[600] }}>
                    Manage your classes and assignments
                  </Typography>
                </Box>
              </Box>
            </ClassroomHeader>
            
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#1a1a1a' }}>
              Your Classes
            </Typography>
            
            <Grid container spacing={3}>
              {assignedClasses.length > 0 ? (
                assignedClasses.map((cls) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={cls.id}>
                    <ClassroomCard 
                      onClick={() => setSelectedClass(cls.id)}
                      sx={{ 
                        cursor: 'pointer',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <StyledAvatar 
                            sx={{ 
                              width: 56, 
                              height: 56,
                              mr: 2,
                              fontSize: 24,
                              fontWeight: 600
                            }}
                          >
                            {cls.name.charAt(0)}
                          </StyledAvatar>
                          <Box>
                            <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                              {cls.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: grey[600] }}>
                              {cls.section || 'Class'}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ color: grey[700], lineHeight: 1.5 }}>
                          {cls.description || 'No description available'}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                        <ModernButton 
                          size="small" 
                          variant="contained"
                          endIcon={<ArrowBackIcon sx={{ transform: 'rotate(180deg)' }} />}
                        >
                          Enter
                        </ModernButton>
                      </CardActions>
                    </ClassroomCard>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#ffffff', borderRadius: 3, border: `1px solid ${grey[200]}` }}>
                    <ClassIcon sx={{ fontSize: 60, color: grey[400], mb: 2 }} />
                    <Typography variant="h6" sx={{ color: grey[600], mb: 1 }}>
                      No classes assigned to you yet
                    </Typography>
                    <Typography variant="body1" sx={{ color: grey[500] }}>
                      Contact your administrator to get assigned to classes
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Box>
        ) : (
          <Box>
            <ClassroomHeader elevation={0}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton 
                  onClick={() => setSelectedClass(null)} 
                  sx={{ 
                    mr: 2,
                    backgroundColor: grey[100],
                    '&:hover': {
                      backgroundColor: grey[200]
                    }
                  }}
                >
                  <ArrowBackIcon sx={{ color: grey[700] }} />
                </IconButton>
                <StyledAvatar 
                  sx={{ 
                    width: 56, 
                    height: 56,
                    mr: 2,
                    fontSize: 24,
                    fontWeight: 600
                  }}
                >
                  {selectedClassData?.name.charAt(0) || 'C'}
                </StyledAvatar>
                <Box>
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}>
                    {selectedClassData?.name || 'Class'}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: grey[600] }}>
                    {selectedClassData?.section || 'Section'} • {selectedClassData?.description || 'Class description'}
                  </Typography>
                </Box>
              </Box>
            </ClassroomHeader>

            <Box sx={{ mb: 3 }}>
              <StyledTabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab 
                  label="Stream" 
                  icon={<AnnouncementIcon />} 
                  iconPosition="start" 
                />
                <Tab 
                  label="Assignments" 
                  icon={<AssignmentIcon />} 
                  iconPosition="start" 
                />
                <Tab 
                  label="People" 
                  icon={<PeopleIcon />} 
                  iconPosition="start" 
                />
                <Tab 
                  label="Grades" 
                  icon={<GradeIcon />} 
                  iconPosition="start" 
                />
              </StyledTabs>
            </Box>

            {activeTab === 0 && (
              <Box>
                <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff', borderRadius: 3, border: `1px solid ${grey[200]}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <StyledAvatar sx={{ mr: 2 }}>
                      {teacherId?.charAt(0).toUpperCase() || 'T'}
                    </StyledAvatar>
                    <TextField
                      fullWidth
                      placeholder="Share something with your class..."
                      onClick={openNewAssignmentDialog}
                      InputProps={{
                        readOnly: true,
                        sx: {
                          borderRadius: 3,
                          backgroundColor: grey[50],
                          '&:hover': {
                            backgroundColor: grey[100],
                          }
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <ModernButton 
                      variant="contained" 
                      startIcon={<PostAddIcon />}
                      onClick={openNewAssignmentDialog}
                    >
                      Create Assignment
                    </ModernButton>
                  </Box>
                </Paper>

                {assignments.length > 0 ? (
                  assignments.map((assignment) => (
                    <AssignmentCard key={assignment.id} sx={{ mb: 3 }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box 
                              sx={{ 
                                backgroundColor: blue[50], 
                                borderRadius: '50%', 
                                p: 1.5, 
                                mr: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <AssignmentIcon sx={{ color: blue[600], fontSize: 24 }} />
                            </Box>
                            <Box>
                              <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}>
                                {assignment.title}
                              </Typography>
                              <Typography variant="body2" sx={{ color: grey[600] }}>
                                Posted: {formatDate(assignment.createdAt)}
                              </Typography>
                            </Box>
                          </Box>
                          <Box>
                            <IconButton 
                              onClick={() => toggleExpand(assignment.id)}
                              sx={{ 
                                backgroundColor: grey[50],
                                '&:hover': { backgroundColor: grey[100] }
                              }}
                            >
                              {expandedAssignments[assignment.id] ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                            <IconButton 
                              onClick={() => startEditingAssignment(assignment)}
                              sx={{ 
                                ml: 1,
                                backgroundColor: grey[50],
                                '&:hover': { backgroundColor: grey[100] }
                              }}
                            >
                              <EditIcon sx={{ color: grey[600] }} />
                            </IconButton>
                            <IconButton 
                              onClick={() => confirmDeleteAssignment(assignment.id)}
                              sx={{ 
                                ml: 1,
                                backgroundColor: red[50],
                                '&:hover': { backgroundColor: red[100] }
                              }}
                            >
                              <DeleteIcon sx={{ color: red[600] }} />
                            </IconButton>
                          </Box>
                        </Box>
                        
                        <Box sx={{ ml: 8, mt: 2 }}>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: grey[800], lineHeight: 1.6 }}>
                            {assignment.content}
                          </Typography>
                          
                          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <ModernButton 
                              variant="outlined" 
                              size="small" 
                              startIcon={<PeopleIcon />}
                              onClick={() => toggleExpand(assignment.id)}
                            >
                              {submissionsByAssignment[assignment.id]?.length || 0} Submissions
                            </ModernButton>
                            <ModernButton 
                              variant="outlined" 
                              size="small" 
                              startIcon={<GradeIcon />}
                            >
                              Grade
                            </ModernButton>
                          </Box>
                        </Box>
                      </CardContent>

                      <Collapse in={expandedAssignments[assignment.id]} timeout="auto" unmountOnExit>
                        <Box sx={{ backgroundColor: grey[50], p: 3, borderTop: `1px solid ${grey[200]}` }}>
                          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#1a1a1a' }}>
                            Student Submissions
                          </Typography>
                          
                          {submissionsByAssignment[assignment.id]?.length > 0 ? (
                            <List sx={{ p: 0 }}>
                              {submissionsByAssignment[assignment.id].map((sub) => (
                                <SubmissionItem key={sub.id}>
                                  <StyledAvatar sx={{ mr: 2, backgroundColor: green[100], color: green[700] }}>
                                    {sub.studentName?.charAt(0) || 'S'}
                                  </StyledAvatar>
                                  <ListItemText
                                    primary={
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                                        {sub.studentName || `Student ${sub.id.substring(0, 6)}`}
                                      </Typography>
                                    }
                                    secondary={
                                      <Box>
                                        <Typography variant="body2" sx={{ color: grey[600], mb: 1 }}>
                                          Submitted: {formatDate(sub.submittedAt)}
                                        </Typography>
                                        {sub.content && (
                                          <Typography variant="body2" sx={{ color: grey[700] }}>
                                            {sub.content}
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                  />
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Tooltip title="Grade submission">
                                      <IconButton 
                                        edge="end"
                                        sx={{ 
                                          backgroundColor: blue[50],
                                          '&:hover': { backgroundColor: blue[100] }
                                        }}
                                      >
                                        <GradeIcon sx={{ color: blue[600] }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </SubmissionItem>
                              ))}
                            </List>
                          ) : (
                            <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#ffffff', borderRadius: 2 }}>
                              <InsertDriveFileIcon sx={{ fontSize: 40, color: grey[400], mb: 1 }} />
                              <Typography variant="h6" sx={{ color: grey[600], mb: 1 }}>
                                No submissions yet
                              </Typography>
                              <Typography variant="body2" sx={{ color: grey[500] }}>
                                Students haven't submitted work for this assignment
                              </Typography>
                            </Paper>
                          )}
                        </Box>
                      </Collapse>
                    </AssignmentCard>
                  ))
                ) : (
                  <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#ffffff', borderRadius: 3, border: `1px solid ${grey[200]}` }}>
                    <AssignmentIcon sx={{ fontSize: 60, color: grey[400], mb: 2 }} />
                    <Typography variant="h6" sx={{ color: grey[600], mb: 1 }}>
                      No assignments created yet
                    </Typography>
                    <Typography variant="body1" sx={{ color: grey[500], mb: 2 }}>
                      Create your first assignment to get started
                    </Typography>
                    <ModernButton 
                      variant="contained" 
                      startIcon={<PostAddIcon />} 
                      onClick={openNewAssignmentDialog}
                    >
                      Create Assignment
                    </ModernButton>
                  </Paper>
                )}
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                    Class Assignments
                  </Typography>
                  <ModernButton 
                    variant="contained" 
                    startIcon={<PostAddIcon />}
                    onClick={openNewAssignmentDialog}
                  >
                    Create
                  </ModernButton>
                </Box>
                
                {assignments.length > 0 ? (
                  <Box>
                    {assignments.map((assignment) => (
                      <AssignmentCard key={assignment.id} sx={{ mb: 2 }}>
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box 
                                sx={{ 
                                  backgroundColor: blue[50], 
                                  borderRadius: '50%', 
                                  p: 1.5, 
                                  mr: 2,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <AssignmentIcon sx={{ color: blue[600], fontSize: 24 }} />
                              </Box>
                              <Box>
                                <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}>
                                  {assignment.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: grey[600] }}>
                                  Due: No due date
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ColorChip 
                                label={`${submissionsByAssignment[assignment.id]?.length || 0} submitted`} 
                                color="primary" 
                                variant="outlined"
                              />
                              <IconButton 
                                onClick={() => startEditingAssignment(assignment)}
                                sx={{ 
                                  backgroundColor: grey[50],
                                  '&:hover': { backgroundColor: grey[100] }
                                }}
                              >
                                <EditIcon sx={{ color: grey[600] }} />
                              </IconButton>
                              <IconButton 
                                onClick={() => confirmDeleteAssignment(assignment.id)}
                                sx={{ 
                                  backgroundColor: red[50],
                                  '&:hover': { backgroundColor: red[100] }
                                }}
                              >
                                <DeleteIcon sx={{ color: red[600] }} />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </AssignmentCard>
                    ))}
                  </Box>
                ) : (
                  <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#ffffff', borderRadius: 3, border: `1px solid ${grey[200]}` }}>
                    <AssignmentIcon sx={{ fontSize: 60, color: grey[400], mb: 2 }} />
                    <Typography variant="h6" sx={{ color: grey[600], mb: 1 }}>
                      No assignments created yet
                    </Typography>
                    <Typography variant="body1" sx={{ color: grey[500], mb: 2 }}>
                      Create your first assignment to get started
                    </Typography>
                    <ModernButton 
                      variant="contained" 
                      startIcon={<PostAddIcon />} 
                      onClick={openNewAssignmentDialog}
                    >
                      Create Assignment
                    </ModernButton>
                  </Paper>
                )}
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                <CreateStudentLink collegeId={collegeId} classId={selectedClass} />
              </Box>
            )}

            {activeTab === 3 && (
              <Box>
                <AnalyticsPage />
              </Box>
            )}

            {activeTab !== 2 && (
              <ClassroomFab 
                color="primary" 
                aria-label="add"
                onClick={() => {
                  if (activeTab === 0 || activeTab === 1) {
                    openNewAssignmentDialog();
                  }
                }}
              >
                {activeTab === 0 || activeTab === 1 ? (
                  <PostAddIcon />
                ) : activeTab === 2 ? (
                  <PersonAddIcon />
                ) : (
                  <AddIcon />
                )}
              </ClassroomFab>
            )}
          </Box>
        )}

        {/* Assignment Dialog */}
        <Dialog
          open={assignmentDialogOpen}
          onClose={cancelEditing}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: `1px solid ${grey[200]}`,
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center',
            borderBottom: `1px solid ${grey[200]}`,
            backgroundColor: '#ffffff'
          }}>
            <Box 
              sx={{ 
                backgroundColor: blue[50], 
                borderRadius: '50%', 
                p: 1, 
                mr: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <AssignmentIcon sx={{ color: blue[600] }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
              {editingAssignment ? 'Edit Assignment' : 'Create Assignment'}
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ backgroundColor: '#ffffff', p: 3 }}>
            <TextField
              fullWidth
              label="Assignment Title"
              value={assignmentTitle}
              onChange={(e) => setAssignmentTitle(e.target.value)}
              margin="normal"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DescriptionIcon sx={{ color: grey[500] }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                  backgroundColor: grey[50],
                }
              }}
              sx={{ mb: 3 }}
            />
            
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#1a1a1a' }}>
              Instructions
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={8}
              value={assignmentContent}
              onChange={(e) => setAssignmentContent(e.target.value)}
              variant="outlined"
              placeholder="Add instructions for your students..."
              InputProps={{
                sx: {
                  borderRadius: 2,
                  backgroundColor: grey[50],
                }
              }}
            />
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#1a1a1a' }}>
                Add materials (coming soon)
              </Typography>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 3, 
                  textAlign: 'center', 
                  backgroundColor: grey[50],
                  borderRadius: 2,
                  borderColor: grey[300],
                  borderStyle: 'dashed'
                }}
              >
                <Typography sx={{ color: grey[600] }}>
                  File upload functionality will be added soon
                </Typography>
              </Paper>
            </Box>
          </DialogContent>
          <DialogActions sx={{ backgroundColor: '#ffffff', p: 3 }}>
            <ModernButton onClick={cancelEditing} variant="outlined">
              Cancel
            </ModernButton>
            <ModernButton
              onClick={editingAssignment ? updateAssignment : createAssignment}
              variant="contained"
              disabled={!assignmentTitle.trim() || !assignmentContent.trim()}
              startIcon={editingAssignment ? <CheckIcon /> : <PostAddIcon />}
            >
              {editingAssignment ? 'Update' : 'Assign'}
            </ModernButton>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: `1px solid ${grey[200]}`,
            }
          }}
        >
          <DialogTitle sx={{ backgroundColor: '#ffffff' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
              Delete Assignment
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: '#ffffff' }}>
            <Typography sx={{ color: grey[700], mb: 2 }}>
              Are you sure you want to delete this assignment? This action cannot be undone.
            </Typography>
            <Typography variant="body2" sx={{ color: grey[600] }}>
              All student submissions for this assignment will also be deleted.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ backgroundColor: '#ffffff', p: 3 }}>
            <ModernButton onClick={() => setDeleteDialogOpen(false)} variant="outlined">
              Cancel
            </ModernButton>
            <Button 
              onClick={deleteAssignment} 
              variant="contained"
              startIcon={<DeleteIcon />}
              sx={{
                backgroundColor: red[600],
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: red[700],
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}